import { CollisionPriority } from "@dnd-kit/abstract";
import { RestrictToVerticalAxis } from "@dnd-kit/abstract/modifiers";
import { move } from "@dnd-kit/helpers";
import { DragDropProvider, KeyboardSensor, PointerSensor } from "@dnd-kit/react";
import { useSortable } from "@dnd-kit/react/sortable";
import type { ProtocolDefinitionDto } from "@repo/data-store";
import { Button } from "@repo/ui/components/ui/button";
import { cn } from "@repo/ui/lib/utils";
import { CalendarDays, GripVertical, Plus, Trash2 } from "lucide-react";
import { useRef, useState } from "react";
import type { DayFormValues, ProtocolFormValues } from "@/features/catalog/forms/use-plan-definition-form";
import { AddCatalogItemButton } from "@/shared/common/add-catalog-item-button";
import { ProtocolCard } from "./protocol-card";

const programSensors = [
  PointerSensor.configure({
    activatorElements(source) {
      return source.handle ? [source.handle] : [source.element];
    },
  }),
  KeyboardSensor,
];

export interface ProgramDaysListProps {
  days: DayFormValues[];
  onDaysChange: (days: DayFormValues[]) => void;
  selectedProtocolId: string | null;
  onSelectProtocol: (protocolId: string) => void;
  onAddDay?: () => void;
  onAddProtocolToDay?: (dayIndex: number) => void;
  protocolPlanDefinitions?: ProtocolDefinitionDto[];
  /** Optional element rendered at the end of the section header row */
  headerAction?: React.ReactNode;
}

export function ProgramDaysList({
  days,
  onDaysChange,
  selectedProtocolId,
  onSelectProtocol,
  onAddDay,
  onAddProtocolToDay,
  protocolPlanDefinitions,
  headerAction,
}: ProgramDaysListProps) {
  const previousDays = useRef<DayFormValues[]>(days);
  // Accumulate drag changes in local state to avoid calling onDaysChange during
  // dnd-kit's drag lifecycle (which uses useInsertionEffect and conflicts with React DOM).
  const [dragDays, setDragDays] = useState<DayFormValues[] | null>(null);
  const renderedDays = dragDays ?? days;

  const handleDragStart = () => {
    previousDays.current = structuredClone(days);
    setDragDays(days);
  };

  // biome-ignore lint/suspicious/noExplicitAny: dnd-kit event types
  const handleDragOver = (event: any) => {
    const currentDays = dragDays ?? days;
    const { source } = event.operation;
    if (source?.type === "day") return;

    const record: Record<string, ProtocolFormValues[]> = {};
    for (const day of currentDays) {
      record[day.id] = day.protocols;
    }
    const newRecord = move(record, event);

    let changed = false;
    for (const day of currentDays) {
      const newProtocols = (newRecord[day.id] as ProtocolFormValues[]) ?? [];
      if (newProtocols.length !== day.protocols.length || newProtocols.some((s, i) => s.id !== day.protocols[i]?.id)) {
        changed = true;
        break;
      }
    }
    if (!changed) return;

    const newDays = currentDays.map((d) => ({
      ...d,
      protocols: (newRecord[d.id] as ProtocolFormValues[]) ?? [],
    }));
    setDragDays(newDays);
  };

  // biome-ignore lint/suspicious/noExplicitAny: dnd-kit event types
  const handleDragEnd = (event: any) => {
    const currentDays = dragDays ?? days;

    if (event.canceled) {
      setDragDays(null);
      onDaysChange(previousDays.current);
      return;
    }

    const { source } = event.operation;
    let finalDays = currentDays;

    if (source?.sortable && source.type === "day") {
      const { initialIndex, index: targetIndex } = source.sortable;
      if (initialIndex !== targetIndex) {
        const reordered = [...currentDays];
        const [moved] = reordered.splice(initialIndex, 1);
        reordered.splice(targetIndex, 0, moved);
        finalDays = reordered;
      }
    }

    // Commit accumulated drag changes
    setDragDays(null);
    onDaysChange(finalDays);
  };

  const sectionHeader = (
    <div className="flex items-center gap-2 min-h-6">
      <h2 className="text-sm font-medium text-muted-foreground">Program Schedule</h2>
      {renderedDays.length > 0 && (
        <span className="flex items-center gap-1 text-xs text-muted-foreground">
          <CalendarDays className="size-3" />
          {renderedDays.length} days
        </span>
      )}
      {headerAction && <div className="ml-auto">{headerAction}</div>}
    </div>
  );

  const addDayButton = <AddCatalogItemButton label="Day" onClick={onAddDay} />;

  if (renderedDays.length === 0) {
    return (
      <div className="space-y-3">
        {sectionHeader}
        {addDayButton}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {sectionHeader}
      <DragDropProvider
        sensors={programSensors}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
      >
        <div className="space-y-4">
          {renderedDays.map((day, dayIndex) => {
            const dayId = day.id;
            const protocols = day.protocols;
            return (
              <SortableDayContainer key={dayId} dayId={dayId} index={dayIndex}>
                {(dayDragHandleRef) => (
                  <>
                    <div className="flex items-center justify-between border-b px-3 h-10 bg-muted/30">
                      <div className="flex items-center gap-2">
                        <div
                          ref={dayDragHandleRef}
                          className="cursor-grab text-muted-foreground hover:text-foreground flex items-center -ml-1"
                        >
                          <GripVertical className="size-4" />
                        </div>
                        <h3 className="text-sm font-medium">{`Day ${dayIndex + 1}`}</h3>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 text-xs"
                          onClick={() => onAddProtocolToDay?.(dayIndex)}
                        >
                          <Plus className="mr-1 size-3" />
                          Add Protocol
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 text-destructive hover:text-destructive"
                          onClick={() => {
                            onDaysChange(renderedDays.filter((_, i) => i !== dayIndex));
                          }}
                        >
                          <Trash2 className="size-3" />
                        </Button>
                      </div>
                    </div>
                    <div className="divide-y">
                      {protocols.length === 0 ? (
                        <p className="text-xs text-muted-foreground text-center py-2 px-3">No protocols</p>
                      ) : (
                        protocols.map((session, sessionIndex) => (
                          <DraggableProtocolCard
                            key={session.id}
                            session={session}
                            sessionIndex={sessionIndex}
                            dayId={dayId}
                            isSelected={selectedProtocolId === session.id}
                            onSelect={() => onSelectProtocol(session.id)}
                            onRemove={() => {
                              const newDays = renderedDays.map((d) => ({ ...d, protocols: [...d.protocols] }));
                              newDays[dayIndex].protocols.splice(sessionIndex, 1);
                              onDaysChange(newDays);
                            }}
                            protocolPlanDefinitions={protocolPlanDefinitions}
                          />
                        ))
                      )}
                    </div>
                  </>
                )}
              </SortableDayContainer>
            );
          })}
        </div>
        {addDayButton}
      </DragDropProvider>
    </div>
  );
}

function SortableDayContainer({
  dayId,
  index,
  children,
}: {
  dayId: string;
  index: number;
  children: (handleRef: (node: HTMLElement | null) => void) => React.ReactNode;
}) {
  const { ref, handleRef, isDragging } = useSortable({
    id: dayId,
    index,
    type: "day",
    accept: ["session", "day"],
    collisionPriority: CollisionPriority.Low,
    modifiers: [RestrictToVerticalAxis],
  });

  return (
    <div ref={ref} className={cn("rounded-lg border", isDragging && "opacity-50")}>
      {children(handleRef)}
    </div>
  );
}

function DraggableProtocolCard({
  session,
  sessionIndex,
  dayId,
  isSelected,
  onSelect,
  onRemove,
  protocolPlanDefinitions,
}: {
  session: ProtocolFormValues;
  sessionIndex: number;
  dayId: string;
  isSelected: boolean;
  onSelect: () => void;
  onRemove: () => void;
  protocolPlanDefinitions?: ProtocolDefinitionDto[];
}) {
  const { ref, handleRef, isDragging } = useSortable({
    id: session.id,
    index: sessionIndex,
    type: "session",
    accept: "session",
    group: dayId,
    modifiers: [RestrictToVerticalAxis],
  });

  return (
    <div ref={ref} className={cn("h-10", isDragging && "opacity-50")}>
      <ProtocolCard
        session={session}
        isSelected={isSelected}
        onSelect={onSelect}
        onRemove={onRemove}
        dragHandleProps={{ ref: handleRef }}
        protocolPlanDefinitions={protocolPlanDefinitions}
      />
    </div>
  );
}
